/**
 * Centralized Lead API Service
 * Handles geolocation fetching and lead submission for all forms across the site
 */

// Fetch user's geolocation data
export const getGeoLocation = async () => {
  try {
    const response = await fetch('https://validationapi.thetechmenders.com/api/GeoLocation/GetGeoLocation', {
      method: 'GET',
      headers: {
        'accept': 'text/plain'
      }
    });
    
    if (!response.ok) {
      console.warn('Geolocation API returned non-OK status:', response.status);
      return null;
    }
    
    const data = await response.json();
    
    if (data.isSuccess && data.data) {
      return data;
    }
    
    return null;
  } catch (error) {
    console.error('Geolocation API Error:', error);
    return null;
  }
};

// Extract numeric phone number only (remove country code prefix and special characters)
const extractPhoneNumber = (phone) => {
  if (!phone) return '';
  // Remove all non-numeric characters
  return phone.replace(/\D/g, '');
};

// Extract country code from full country code string
const extractCountryDialCode = (countryCode) => {
  if (!countryCode) return 1;
  // Remove '+' and any non-numeric characters
  const numeric = countryCode.replace(/\D/g, '');
  return parseInt(numeric) || 1;
};

/**
 * Submit lead to CRM API
 * @param {Object} formData - Form data from user
 * @param {Object} geoLocationData - Geolocation data from API
 * @param {Object} packageInfo - Optional package information {title, amount}
 * @param {string} buttonContext - Context of which button/form was used
 * @param {number} tmSignUpType - 1 for Self Filing, 2 for Attorney-Led Filing
 * @returns {Promise<Object>} Result object with success status and message
 */
export const submitLead = async (formData, geoLocationData = null, packageInfo = null, buttonContext = '', tmSignUpType = null) => {
  try {
    // Extract data from geolocation if available
    const city = geoLocationData?.data?.cityName || '';
    const state = geoLocationData?.data?.regionName || '';
    const ipAddress = geoLocationData?.data?.ipAddress || '';
    
    // Get country from geolocation or form data
    const country = geoLocationData?.data?.countryCode || formData.country || 'US';
    
    // Build description with context
    let description = formData.description || 'IP protection inquiry';
    if (buttonContext) {
      description = `${buttonContext}\n\n${description}`;
    }
    
    // Prepare payload according to API spec
    const payload = {
      name: formData.name || formData.fullName || '',
      email: formData.email || '',
      countryDialCode: extractCountryDialCode(formData.countryCode),
      country: country,
      phoneNumber: extractPhoneNumber(formData.phone),
      ipAddress: ipAddress,
      description: description,
      pageUrl: window.location.origin + window.location.pathname,
      city: city,
      state: state,
      geoLocation: geoLocationData ? JSON.stringify(geoLocationData) : '',
      packageTitle: packageInfo?.title || '',
      packageAmount: packageInfo?.amount || 0,
      tmSignUpType: tmSignUpType || null
    };

    console.log('Submitting lead with payload:', payload);

    const response = await fetch('https://leadapi.crmbytes.com/api/Lead/AddLead', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'accept': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();
    console.log('Lead API Response:', data);

    // Handle different status codes as per spec
    if (response.status === 200 && data.isSuccess) {
      // Success - redirect to application page
      if (data.data?.customerId) {
        const redirectUrl = `/thanks?customerid=${data.data.customerId}`;
        console.log('Redirecting to:', redirectUrl);
        window.location.href = redirectUrl;
      }
      return {
        success: true,
        message: data.data?.message || 'Lead created successfully',
        data: data.data
      };
    } else if (response.status === 401) {
      // Unauthorized - show message from API
      return {
        success: false,
        message: data.message || 'Unauthorized access'
      };
    } else if (response.status === 400) {
      // Bad Request - show errorMessage from API
      return {
        success: false,
        message: data.errorMessage || data.data?.message || 'Invalid request data'
      };
    } else {
      // Other errors
      return {
        success: false,
        message: data.errorMessage || data.message || 'Something went wrong. Please try again.'
      };
    }
  } catch (error) {
    console.error('Lead Submission Error:', error);
    return {
      success: false,
      message: 'Failed to submit. Please check your connection and try again.'
    };
  }
};

/**
 * Complete flow: Get geolocation, then submit lead
 * This is the main function to call from forms
 * @param {Object} formData - Form data from user
 * @param {Object} packageInfo - Optional package information {title, amount}
 * @param {string} buttonContext - Context of which button/form was used
 * @param {number} tmSignUpType - 1 for Self Filing, 2 for Attorney-Led Filing
 * @returns {Promise<Object>} Result object with success status and message
 */
export const submitLeadWithGeoLocation = async (formData, packageInfo = null, buttonContext = '', tmSignUpType = null) => {
  try {
    // First, get geolocation
    const geoLocationData = await getGeoLocation();

    if (geoLocationData) {
      console.log('Geolocation fetched successfully:', geoLocationData.data?.countryName);
    } else {
      console.warn('Could not fetch geolocation, proceeding without it');
    }

    // Then submit lead with geolocation data
    const result = await submitLead(formData, geoLocationData, packageInfo, buttonContext, tmSignUpType);

    return result;
  } catch (error) {
    console.error('Submit Lead with Geolocation Error:', error);
    return {
      success: false,
      message: 'An error occurred. Please try again.'
    };
  }
};

/**
 * Get country information from geolocation for display purposes
 * @returns {Promise<Object>} Country info {countryCode, countryName, flag}
 */
export const getCountryInfo = async () => {
  try {
    const geoData = await getGeoLocation();
    
    if (geoData?.data) {
      return {
        countryCode: geoData.data.countryCode,
        countryName: geoData.data.countryName,
        region: geoData.data.regionName,
        city: geoData.data.cityName
      };
    }
    
    return null;
  } catch (error) {
    console.error('Get Country Info Error:', error);
    return null;
  }
};
